/// <amd-module name="Repos/Common/VCViewModelDataProviderService" />
import { GitRepository, GitVersionDescriptor, GitVersionType } from "Repos/Common/Contracts/Git";
import { IVersionControlUserPreferences, IVersionControlViewModel } from "Repos/Common/Contracts/VersionControlViewModel";
import { getTFSProject } from "Tfs/Platform/Page";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { refToBranchName } from "./Util/Ref";

const versionControlViewModelDataProviderId = "ms.vss-code-web.versioncontrol-viewmodel-data-provider";

export interface IVCViewModelDataProviderService extends IVssService {
    getDefaultGitBranchName(): string | undefined;
    getRepository(): GitRepository | undefined;
    getUserPreferences(): IVersionControlUserPreferences;
    getViewModel(): IVersionControlViewModel;
    setUserDefaultBranch(branch: string): void;
    setUserPreferences(preferences: IVersionControlUserPreferences): Promise<Response>;
    supportsGit(): boolean;
    supportsTFVC(): boolean;
}

class VCViewModelDataProviderService extends VssService implements IVCViewModelDataProviderService {
    private userPreferences: IVersionControlUserPreferences | undefined;

    public getDefaultGitBranchName(): string | undefined {
        const viewModelData = this.getViewModel();
        let branchName = viewModelData.defaultGitBranchName;
        if (!branchName && viewModelData.gitRepository && viewModelData.gitRepository.defaultBranch) {
            branchName = refToBranchName(viewModelData.gitRepository.defaultBranch);
        }
        return branchName;
    }

    public getRepository(): GitRepository | undefined {
        const viewModelData = this.getViewModel();
        return viewModelData.gitRepository;
    }

    public getUserPreferences(): IVersionControlUserPreferences {
        if (!this.userPreferences) {
            this.userPreferences = this.getViewModel().vcUserPreferences;
        }
        return this.userPreferences;
    }

    public getViewModel(): IVersionControlViewModel {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const viewModelData = contributionService.getData<IVersionControlViewModel>(versionControlViewModelDataProviderId);
        if (!viewModelData) {
            throw new Error(`Data provider data "${versionControlViewModelDataProviderId}" not present.`);
        }
        return viewModelData;
    }

    public setUserDefaultBranch(branchName: string): void {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries({ "Git/DefaultUserBranch": branchName }, SettingsUserScope.Me, "Repository", this.getRepository()!.id);
    }

    public setUserPreferences(preferences: IVersionControlUserPreferences): Promise<Response> {
        if (!userHasClaim(this.pageContext, UserClaims.Member)) {
            return Promise.resolve(new Response());
        }

        this.userPreferences = preferences;

        // Persist the new preferences to the server. This is a very old MVC _api endpoint
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath || "";
        const project = getTFSProject(this.pageContext);
        const requestUrl = combineUrlPaths(encodeURI(hostPath), `${project ? project.id + "/" : ""}_api/_versioncontrol/updateUserPreferences?__v=5`);
        const verificationInput = document.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]");
        const verificationToken = verificationInput ? verificationInput.value : "";

        return issueRequest(requestUrl, {
            method: "POST",
            body: `preferences=${encodeURIComponent(JSON.stringify(preferences))}&__RequestVerificationToken=${encodeURIComponent(
                verificationToken
            )}`,
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            }
        });
    }

    public supportsGit(): boolean {
        const viewModelData = this.getViewModel();
        return viewModelData.projectVersionControlInfo.supportsGit;
    }

    public supportsTFVC(): boolean {
        const viewModelData = this.getViewModel();
        return viewModelData.projectVersionControlInfo.supportsTFVC;
    }
}

Services.add("IVCViewModelDataProviderService", { serviceFactory: VCViewModelDataProviderService });

/**
 * Gets the currently selected git repository for the current page
 *
 * @param pageContext Page context
 */
export function getGitRepository(pageContext: IVssPageContext): GitRepository | undefined {
    return pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService").getRepository();
}

/**
 * Sets the user preferred branch if the given version is of Branch type
 *
 * @param pageContext Page context
 * @param newVersion New version to store
 */
export function setUserDefaultVersion(pageContext: IVssPageContext, newVersion: GitVersionDescriptor) {
    if (newVersion.versionType === GitVersionType.Branch) {
        pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService").setUserDefaultBranch(newVersion.version);
    }
}
